// ✅ src/pages/VehicleV2/VehicleTransferReport_Print.jsx
// Print fix: A4 landscape + fit-to-page table, no clipped columns

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/vehicleTransferReport";

const toStr = (v) => String(v ?? "").trim();
const pad2 = (n) => String(n).padStart(2, "0");

const fmtDate = (isoOrDate) => {
  if (!isoOrDate) return "";
  if (typeof isoOrDate === "string" && isoOrDate.includes("/")) return isoOrDate;
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtNum2 = (v) => {
  if (v === null || v === undefined || v === "") return "0.00";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function normalizeRow(x, idx) {
  return {
    sno: x?.sno ?? idx + 1,
    vehicleMainType: x?.vehicleMainType ?? x?.VehicleMainType ?? x?.fld_vehiclemaintype ?? "",
    vehicleCode: x?.vehicleCode ?? x?.VehicleCode ?? x?.fld_vehicle_code ?? "",
    vehicleRegNo: x?.vehicleRegNo ?? x?.VehicleRegNo ?? x?.fld_vehicle_regno ?? "",
    transferFrom: x?.transferFrom ?? x?.TransferFrom ?? "",
    transferTo: x?.transferTo ?? x?.TransferTo ?? "",
    transferDate: fmtDate(x?.transferDate ?? x?.TransferDate),
    transferBy: x?.transferBy ?? x?.TransferBy ?? "",
    approvedDate: fmtDate(x?.approvedDate ?? x?.ApprovedDate),
    approvedBy: x?.approvedBy ?? x?.ApprovedBy ?? "",
    recevDate: fmtDate(x?.fbRecDate ?? x?.recevDate ?? x?.RecevDate ?? x?.FBRecDate),
    workHr: fmtNum2(x?.workHr ?? x?.WorkHR ?? x?.WorkHr ?? 0),
    cumHr: fmtNum2(x?.cumHr ?? x?.CUMHR ?? x?.CumulativeHr ?? 0),
  };
}

const COLS = [
  { key: "sno", label: "S.No", w: "4%" },
  { key: "vehicleMainType", label: "Vehicle Main Type", w: "12%" },
  { key: "vehicleCode", label: "Vehicle Code", w: "7%" },
  { key: "vehicleRegNo", label: "Reg No.", w: "8%" },
  { key: "transferFrom", label: "Transfer From", w: "8%" },
  { key: "transferTo", label: "Transfer To", w: "8%" },
  { key: "transferDate", label: "Transfer Date", w: "8%" },
  { key: "transferBy", label: "Transfer By", w: "7%" },
  { key: "approvedDate", label: "Appr. Date", w: "8%" },
  { key: "approvedBy", label: "Appr By", w: "7%" },
  { key: "recevDate", label: "Recev. Date", w: "8%" },
  { key: "workHr", label: "Working Hr", w: "7%", align: "right" },
  { key: "cumHr", label: "Cumulative Hr", w: "8%", align: "right" },
];

export default function VehicleTransferReportPrint() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const params = useMemo(() => {
    const obj = Object.fromEntries([...searchParams.entries()]);
    return {
      type: (obj.type || "B").toUpperCase() === "L" ? "L" : "B",
      fromBranchId: obj.fromBranchId || "0",
      toBranchId: obj.toBranchId || "0",
      fromDate: obj.fromDate || "",
      toDate: obj.toDate || "",
      vehicleName: obj.vehicleName || "",
      vehicleRegistrationNumber: obj.vehicleRegistrationNumber || "",
      vehicleCode: obj.vehicleCode || "",
      vehicleMainTypeId: obj.vehicleMainTypeId || "0",
    };
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await axios.get(`${API_BASE}/list`, { params });
        if (!alive) return;
        setRows(Array.isArray(res?.data?.rows) ? res.data.rows : []);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [params]);

  const data = useMemo(() => rows.map(normalizeRow), [rows]);
  const totalWorkHr = useMemo(
    () => data.reduce((sum, r) => sum + (Number(String(r.workHr).replace(/,/g, "")) || 0), 0),
    [data]
  );

  return (
    <div className="report-page w-full bg-white">
      <style>{`
        .report-page { color: #111; font-family: "Segoe UI", Arial, sans-serif; }
        .rpt-table {
          width: 100%;
          table-layout: fixed;
          border-collapse: collapse;
        }
        .rpt-table th,
        .rpt-table td {
          border: 1px solid #000;
          padding: 3px 2px;
          font-size: 7.5px;
          line-height: 1.2;
          word-break: break-word;
          overflow-wrap: anywhere;
          white-space: normal;
          vertical-align: top;
        }
        .rpt-table th { background: #e5e7eb; font-weight: 700; text-align: center; }
        .no-print-scroll { width: 100%; overflow: visible; }

        @media print {
          @page { size: A4 landscape; margin: 6mm; }
          html, body {
            width: 100% !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print { display: none !important; }
          .report-sheet { margin: 0 !important; padding: 0 !important; width: 100% !important; }
          .no-print-scroll { overflow: visible !important; }
          .rpt-table { width: 100% !important; table-layout: fixed !important; }
          .rpt-table thead { display: table-header-group; }
          .rpt-table tbody tr { break-inside: avoid; }
        }
      `}</style>

      <div className="report-sheet p-3 print:p-0">
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50" type="button">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            type="button"
          >
            Print
          </button>
        </div>

        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-16 h-14 border border-black flex items-center justify-center mr-3 bg-gray-800 text-white font-bold">
              <div className="text-center text-[10px]">
                <div>NISSI</div>
                <div>INFOTECH</div>
              </div>
            </div>
            <div className="flex-1 text-center leading-tight">
              <h1 className="text-base font-bold">Nissi Infotech</h1>
              <div className="text-xs font-semibold">Nissi CIVIL CONSTRUCTION</div>
              <div className="text-[10px] mt-1">
                3/42-12, Nagappa Gounder St, kavundampalayam, Coimbatore - Tamil Nadu.
              </div>
              <div className="text-sm font-bold underline mt-1">VEHICLE TRANSFER REPORT</div>
            </div>
            <div className="w-40 text-right text-[10px]">
              <div>INFRA SCREEN : B-46</div>
              <div>
                From: <b>{toStr(params.fromDate) || "-"}</b>
              </div>
              <div>
                To: <b>{toStr(params.toDate) || "-"}</b>
              </div>
            </div>
          </div>
        </div>

        <div className="no-print-scroll">
          <table className="rpt-table">
            <thead>
              <tr>
                {COLS.map((c) => (
                  <th key={c.key} style={{ width: c.w }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!data.length ? (
                <tr>
                  <td colSpan={COLS.length} style={{ textAlign: "center" }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                data.map((r) => (
                  <tr key={`${r.vehicleCode}-${r.sno}`}>
                    {COLS.map((c) => (
                      <td key={c.key} style={{ textAlign: c.align || (c.key === "sno" ? "center" : "left") }}>
                        {toStr(r[c.key])}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            {data.length ? (
              <tfoot>
                <tr>
                  <td colSpan={11} style={{ textAlign: "right", fontWeight: 700 }}>
                    Total
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>{fmtNum2(totalWorkHr)}</td>
                  <td />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>

        <div className="mt-3 border-2 border-black p-1 text-xs text-center">
          Report Created by Infracosoft version 2.1. www.infracosoft.com
        </div>
      </div>
    </div>
  );
}